import { convertToSecretCode } from '../quarter-selector.constants';
import { QuarterSelectorType, QuarterSelectorValue } from '@miq/shared-models';
export interface MonthOptions {
  showAsQtd: boolean;
  minMonthValue: number;
  maxMonthValue: number;
}
const defaultMonthOptions: MonthOptions = {
  showAsQtd: false,
  minMonthValue: 0, // When human created the computer
  maxMonthValue: 205012, // December of 2050
};
export class Month {
  public value = 0;
  private options: MonthOptions = defaultMonthOptions;
  public disabled: boolean;
  private qtdValue = 0;
  public marketMonthQtdValue: QuarterSelectorValue | Date;
  constructor(
    public name: string,
    public monthNumber: number,
    public year = 1
  ) {
    this.createValue();
  }
  public setYear(year: number) {
    this.year = year;
    this.createValue();
    this.processOptions();
  }
  public setOptions(options: MonthOptions) {
    this.options = { ...defaultMonthOptions, ...options };
    if (this.options.showAsQtd) {
      this.qtdValue = this.getQtdMonth();
    }
    this.processOptions();
  }
  private getQtdMonth() {
    const isDate = this.marketMonthQtdValue && this.marketMonthQtdValue instanceof Date;
    if (!isDate) {
      return convertToSecretCode(
        (this.marketMonthQtdValue as QuarterSelectorValue).periodYear,
        (this.marketMonthQtdValue as QuarterSelectorValue).periodValue * 3,
        QuarterSelectorType.Monthly
      );
    } else {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      return convertToSecretCode(year, month, QuarterSelectorType.Monthly);
    }
  }
  private processOptions() {
    if (this.options.showAsQtd) {
      this.options.maxMonthValue = this.qtdValue;
    }
    this.disabled = this.value > this.options.maxMonthValue || this.value <= this.options.minMonthValue;
  }
  private createValue() {
    // The value of the month is the year concatenated with month
    // values for months {year}{month} -> 202001, 201912
    this.value = convertToSecretCode(this.year, this.monthNumber, QuarterSelectorType.Monthly);
  }
}
export interface QuarterOptions extends MonthOptions {
  showAsQtd: boolean;
  minQuarterValue: number;
  maxQuarterValue: number;
  allowedPeriods: Set<number>;
  restrictedPeriods: Set<number>;
}
const defaultQuarterOptions: QuarterOptions = {
  showAsQtd: false,
  minQuarterValue: 0, // The origin of the times
  maxQuarterValue: 2050004, // Last quarter of 2050
  allowedPeriods: null,
  restrictedPeriods: null,
  ...defaultMonthOptions,
};
export class Quarter {
  public value = 0;
  months: Month[] = [];
  public name: string;
  private options: QuarterOptions = defaultQuarterOptions;
  public disabled: boolean;
  public marketQtdValue: QuarterSelectorValue | Date;
  constructor(
    public quarterNumber: number,
    public year = 1
  ) {
    this.name = 'Q' + quarterNumber;
    this.createValue();
  }
  public setOptions(options: QuarterOptions) {
    this.options = { ...defaultQuarterOptions, ...options };
    this.processOptions();
  }
  public setYear(year: number) {
    this.year = year;
    this.createValue();
    this.months.forEach(x => {
      x.marketMonthQtdValue = this.marketQtdValue;
      x.setOptions(this.options);
      x.setYear(year);
    });
    this.processOptions();
  }
  private getQtdValueByMarket(): string {
    const isDate = this.marketQtdValue && this.marketQtdValue instanceof Date;
    let name: string;
    if (isDate) {
      name = 'Q' + this.quarterNumber;
    } else if (
      this.quarterNumber == (this.marketQtdValue as QuarterSelectorValue).periodValue &&
      (this.marketQtdValue as QuarterSelectorValue).periodYear == this.year
    ) {
      name = 'QTD';
    } else {
      name = 'Q' + this.quarterNumber;
    }
    return name;
  }
  private processOptions() {
    if (this.options.showAsQtd) {
      this.name = this.getQtdValueByMarket();
    }
    const disableByNerdCalculations =
      this.value > this.options.maxQuarterValue || this.value <= this.options.minQuarterValue;
    const disableByConfiguration = this.shouldThisBeDisabledByConfiguration();
    this.disabled = disableByNerdCalculations || disableByConfiguration;
  }
  private shouldThisBeDisabledByConfiguration() {
    let disableNotAllowed = false;
    let disableRestricted = false;
    if (this.options.allowedPeriods != null) {
      disableNotAllowed = !this.options.allowedPeriods.has(this.value);
    }
    if (this.options.restrictedPeriods != null) {
      disableRestricted = this.options.restrictedPeriods.has(this.value);
    }
    return disableNotAllowed || disableRestricted;
  }
  private createValue() {
    // The value of the quarter is the year + 00 + quarter
    // to avoid collision with month values -> 2020001, 2019004
    this.value = convertToSecretCode(this.year, this.quarterNumber, QuarterSelectorType.Quarter);
  }
}
